import { Box, FormHelperText, IconButton, TextField, Tooltip } from '@mui/material'
import { FeatureFlagIdCopilotAgents } from 'c1gen/features'
import { C1LoadingButton } from 'c1ui'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { HasFeature } from 'components/no-ui/capability'
import { Sparkles, X } from 'lucide-react'
import { GetHelperServiceRequest } from 'pbts/c1/api/copilot_cel/v1/private/copilot_cel'
import { EditorValidationEnv } from 'pbts/c1/api/editor/v1/editor'
import { FC, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getCELHelper } from 'redux_toolkit/copilot-cel'
import { CELChatAssistant } from './CELChatAssistant'

/**
 * Props for the CEL prompter components.
 */
type CopilotCELPrompterProps = {
    validationEnv: EditorValidationEnv
    setExpression: (expression: string) => void
    setExplainerText: React.Dispatch<React.SetStateAction<string>>
    /** Current expression in the editor, used for refinement in chat mode */
    currentExpression?: string
    /** Callback when user closes the prompter */
    onClose?: () => void
}

/**
 * Main entry point for CEL expression AI assistance.
 *
 * - When COPILOT_AGENTS feature flag is enabled: renders CELChatAssistant (conversation-based)
 * - When feature flag is disabled: renders CopilotCELPrompterLegacy (single-shot)
 */
export const CopilotCELPrompter: FC<CopilotCELPrompterProps> = ({
    validationEnv,
    setExpression,
    setExplainerText,
    currentExpression,
    onClose,
}) => {
    const handleExpressionApply = useCallback((expression: string): void => {
        setExpression(expression)
        // setExplainerText is not used in the new UI, since the agent will provide the explanation inline.
        // setExplainerText(explanation)
    }, [setExpression])

    const legacyUI = <CopilotCELPrompterLegacy
        validationEnv={validationEnv}
        setExpression={setExpression}
        setExplainerText={setExplainerText}
        onClose={onClose}
    />

    return <HasFeature feature={FeatureFlagIdCopilotAgents} fallback={legacyUI}>
        <Box>
            <CELChatAssistant
                validationEnv={validationEnv}
                initialExpression={currentExpression}
                onExpressionApply={handleExpressionApply}
                onClose={onClose}
            />
        </Box>
    </HasFeature>
}

/**
 * Legacy single-shot CEL expression prompter.
 * This is rendered when the COPILOT_AGENTS feature flag is disabled.
 */
const CopilotCELPrompterLegacy: FC<Omit<CopilotCELPrompterProps, 'currentExpression'>> = ({
    validationEnv,
    setExpression,
    setExplainerText,
    onClose,
}) => {
    const {
        trigger,
        data,
        error,
        isError,
        isLoading,
    } = getCELHelper.useLazyGet()

    const [promptValue, setPromptValue] = useState('')

    const onSubmit = useCallback(async (event: React.FormEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault()
        const req = GetHelperServiceRequest.create({
            prompt: promptValue,
            validationEnv,
        })

        trigger(req, { force: true })
    }, [promptValue, validationEnv, trigger])

    useEffect(() => {
        if (data) {
            setExpression(data.expression)
            setExplainerText(data.explanation)
        }
    }, [data, setExpression, setExplainerText])

    const intl = useIntl()

    return <Box sx={{ mb: 1 }}>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
            }}
        >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder={intl.formatMessage({
                        id: 'components_core_copilot_cel_prompter_placeholder',
                        defaultMessage: 'Describe the expression you need...',
                    })}
                    value={promptValue}
                    disabled={isLoading}
                    onChange={(e): void => setPromptValue(e.target.value)}
                    onKeyDown={(e): void => {
                        if (e.key === 'Enter' && !e.shiftKey && promptValue.trim()) {
                            e.preventDefault()
                            onSubmit(e as unknown as React.FormEvent<HTMLButtonElement>)
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'common.white',
                        },
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <C1LoadingButton
                        variant="contained"
                        onClick={onSubmit}
                        disabled={isLoading || !promptValue.trim()}
                        loading={isLoading}
                        startIcon={<C1Icon icon={Sparkles} />}
                    >
                        <FormattedMessage
                            id="components_core_copilot_cel_prompter_submit"
                            defaultMessage="Generate"
                        />
                    </C1LoadingButton>
                    {onClose && <Tooltip title={<FormattedMessage id="close" defaultMessage="Close" />}>
                        <IconButton size="small" onClick={onClose}>
                            <C1Icon icon={X} size="sm" />
                        </IconButton>
                    </Tooltip>}
                </Box>
            </Box>
        </Box>

        {isError && <Box sx={{ mt: 1 }}>
            <FormHelperText error>
                <FormattedMessage
                    id="components_core_copilot_cel_prompter_error"
                    defaultMessage="Prompt failed: "
                />
            </FormHelperText>
            <APIError err={error} />
        </Box>}
    </Box>
}
